<template>
  <div class="flex flex-col w-full h-full gap-2 overflow-y-auto">
    <!-- Code -->
    <span class="mb-1 font-medium">קוד הקופון</span>
    <InputText
      v-model="form.code"
      placeholder="למשל: SUMMER20"
      class="uppercase"
      @input="onCodeInput"
    />
    <span class="text-xs text-gray-500">הלקוחות יזינו את הקוד הזה בעת קביעת התור</span>

    <!-- Discount type -->
    <span class="mt-3 mb-1 font-medium">סוג ההנחה</span>
    <div class="flex gap-2">
      <button
        type="button"
        class="flex-1 py-2.5 rounded-lg border-2 text-sm font-medium transition-colors"
        :class="form.discountType === CouponDiscountTypeEnum.Percentage
          ? 'border-primary bg-primary-50 text-primary'
          : 'border-gray-200 bg-white text-gray-600'"
        @click="form.discountType = CouponDiscountTypeEnum.Percentage"
      >% אחוז</button>
      <button
        type="button"
        class="flex-1 py-2.5 rounded-lg border-2 text-sm font-medium transition-colors"
        :class="form.discountType === CouponDiscountTypeEnum.Amount
          ? 'border-primary bg-primary-50 text-primary'
          : 'border-gray-200 bg-white text-gray-600'"
        @click="form.discountType = CouponDiscountTypeEnum.Amount"
      >₪ סכום</button>
    </div>

    <!-- Discount value -->
    <template v-if="form.discountType === CouponDiscountTypeEnum.Percentage">
      <span class="mt-3 mb-1 font-medium">אחוז ההנחה</span>
      <input-number v-model="form.discountValue" :min="1" :max="100" suffix="%" placeholder="למשל: 20" />

      <span class="mt-3 mb-1 font-medium">תקרת הנחה (לא חובה)</span>
      <input-number
        v-model="form.maxDiscountAmount"
        :min="1"
        mode="currency"
        currency="ILS"
        locale="he-IL"
        placeholder="הנחה מקסימלית בש״ח"
      />
    </template>
    <template v-else>
      <span class="mt-3 mb-1 font-medium">סכום ההנחה</span>
      <input-number
        v-model="form.discountValue"
        :min="1"
        :maxFractionDigits="0"
        mode="currency"
        currency="ILS"
        locale="he-IL"
        placeholder="למשל: 50"
      />
    </template>

    <!-- Minimum purchase -->
    <span class="mt-3 mb-1 font-medium">סכום מינימום להזמנה (לא חובה)</span>
    <input-number
      v-model="form.minPurchaseAmount"
      :min="0"
      mode="currency"
      currency="ILS"
      locale="he-IL"
      placeholder="ללא מינימום"
    />

    <!-- What the coupon may be spent on -->
    <span class="mt-3 mb-1 font-medium">על מה הקופון חל (לא חובה)</span>
    <div class="flex flex-wrap gap-2">
      <button
        v-for="option in entityTypeOptions"
        :key="option.value"
        type="button"
        class="px-3 py-1.5 rounded-full border text-sm transition-colors"
        :class="isEntityTypeSelected(option.value)
          ? 'bg-primary text-white border-primary'
          : 'bg-white text-surface-600 border-surface-300'"
        @click="toggleEntityType(option.value)"
      >
        {{ option.label }}
      </button>
    </div>
    <span class="text-xs text-gray-500">השאר ריק כדי שהקופון יחול על הכל</span>

    <!-- Applicable services — only meaningful while services are covered -->
    <template v-if="supportsServiceScope">
      <span class="mt-3 mb-1 font-medium">שירותים (לא חובה)</span>
      <select-service v-model="selectedServices" @services-selected="onServicesSelected" />
      <span class="text-xs text-gray-500">השאר ריק כדי שהקופון יחול על כל השירותים</span>
    </template>

    <!-- Validity window -->
    <div class="flex gap-3 mt-3">
      <div class="flex flex-col flex-1 gap-1">
        <span class="font-medium">בתוקף מ-</span>
        <ResponsiveDatePicker v-model="form.validFrom" title="בתוקף מ-" />
      </div>
      <div class="flex flex-col flex-1 gap-1">
        <span class="font-medium">בתוקף עד</span>
        <ResponsiveDatePicker v-model="form.validUntil" :minDate="new Date()" title="בתוקף עד" />
      </div>
    </div>

    <!-- Usage limits -->
    <span class="mt-3 mb-1 font-medium">מגבלת שימוש כוללת (לא חובה)</span>
    <input-number v-model="form.usageLimit" :min="1" :maxFractionDigits="0" placeholder="ללא הגבלה" />

    <span class="mt-3 mb-1 font-medium">מגבלת שימוש ללקוח</span>
    <input-number v-model="form.perCustomerLimit" :min="1" :maxFractionDigits="0" placeholder="ללא הגבלה" />

    <settings-toggle
      class="mt-3"
      :status="form.firstTimeCustomersOnly ?? false"
      title="ללקוחות חדשים בלבד"
      @value-changed="(val) => form.firstTimeCustomersOnly = val"
    />

    <settings-toggle
      class="mt-2"
      :status="form.active ?? true"
      title="פעיל"
      @value-changed="(val) => form.active = val"
    />

    <Button
      v-if="isEdit"
      class="mt-4"
      severity="danger"
      text
      label="מחיקת הקופון"
      :loading="deleting"
      @click="remove"
    />

    <drawer-footer
      :class="{ 'mt-auto': !isEdit }"
      :approveDisable="!canSubmit"
      :approveLoading="loading"
      :approve-label="isEdit ? 'שמירת שינויים' : 'יצירת קופון'"
      @approve-click="submit"
      @cancel-click="$emit('close')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, PropType, reactive, ref } from 'vue';
import {
  COUPON_ENTITY_TYPE_LABELS,
  CouponDiscountTypeEnum,
  CouponEntityTypeEnum,
  couponSupportsServiceScope,
  ICoupon,
  IService
} from '@torim-app/types';
import SelectService from '@app/components/admin/components/SelectService.vue';
import { PromotionsApi } from '@app/api/admin/promotions-api';
import { useToast } from 'vue-toastification';
import { useAdminStore } from '@app/stores';
import { useConfirm } from 'primevue/useconfirm';
import ResponsiveDatePicker from '@app/components/admin/components/ResponsiveDatePicker.vue';
import { trackEvent } from '@app/composables/posthog';

const props = defineProps({
  coupon: {
    type: Object as PropType<ICoupon | null>,
    required: false,
    default: null
  }
});

const emit = defineEmits(['close', 'created', 'updated', 'deleted']);
const toast = useToast();
const adminStore = useAdminStore();
const confirm = useConfirm();

const isEdit = computed(() => !!props.coupon?._id);

const form = reactive<Partial<ICoupon> & { validFrom?: Date | string; validUntil?: Date | string }>({
  code: '',
  description: '',
  discountType: CouponDiscountTypeEnum.Percentage,
  discountValue: undefined,
  maxDiscountAmount: undefined,
  minPurchaseAmount: undefined,
  applicableEntityTypes: [],
  applicableServiceIds: [],
  validFrom: undefined,
  validUntil: undefined,
  usageLimit: undefined,
  perCustomerLimit: 1,
  firstTimeCustomersOnly: false,
  active: true
});

const selectedServices = ref<IService[]>([]);
const loading = ref(false);
const deleting = ref(false);

const onCodeInput = () => {
  form.code = (form.code ?? '').toUpperCase().replace(/\s+/g, '');
};

const onServicesSelected = (services: IService[]) => {
  selectedServices.value = services;
  form.applicableServiceIds = services.map(s => s.id);
};

const entityTypeOptions = (Object.values(CouponEntityTypeEnum) as CouponEntityTypeEnum[])
  .map(value => ({ value, label: COUPON_ENTITY_TYPE_LABELS[value] }));

const isEntityTypeSelected = (type: CouponEntityTypeEnum) =>
  (form.applicableEntityTypes ?? []).includes(type);

// Nothing selected means "everything", so services stay in scope until the owner picks a
// set that leaves them out.
const supportsServiceScope = computed(() => couponSupportsServiceScope(form));

const toggleEntityType = (type: CouponEntityTypeEnum) => {
  const current = form.applicableEntityTypes ?? [];
  form.applicableEntityTypes = current.includes(type)
    ? current.filter(t => t !== type)
    : [...current, type];

  // A per-service restriction can't survive services leaving scope — drop it here so the
  // hidden selector can't submit a combination the backend rejects.
  if (!couponSupportsServiceScope(form)) {
    selectedServices.value = [];
    form.applicableServiceIds = [];
  }
};

const canSubmit = computed(() =>
  !!form.code?.trim() && !!form.discountValue && form.discountValue > 0
);

const buildBody = (): Partial<ICoupon> & {
  validFrom?: string | null;
  validUntil?: string | null;
  maxDiscountAmount?: number | null;
} => ({
  code: form.code?.trim().toUpperCase(),
  description: form.description,
  discountType: form.discountType,
  discountValue: form.discountValue,
  // Send explicit null (not undefined, which JSON.stringify drops) so cleared fields actually persist.
  maxDiscountAmount: form.discountType === CouponDiscountTypeEnum.Percentage ? (form.maxDiscountAmount ?? null) : null,
  minPurchaseAmount: form.minPurchaseAmount,
  applicableEntityTypes: form.applicableEntityTypes ?? [],
  // The backend rejects a service list on a coupon that doesn't cover services.
  applicableServiceIds: supportsServiceScope.value ? (form.applicableServiceIds ?? []) : [],
  validFrom: form.validFrom ? new Date(form.validFrom).toISOString() : null,
  validUntil: form.validUntil ? new Date(form.validUntil).toISOString() : null,
  usageLimit: form.usageLimit,
  perCustomerLimit: form.perCustomerLimit,
  firstTimeCustomersOnly: form.firstTimeCustomersOnly,
  active: form.active
});

const submit = async () => {
  if (!canSubmit.value) return;
  loading.value = true;
  try {
    if (isEdit.value && props.coupon?._id) {
      const updated = await PromotionsApi.updateCoupon(props.coupon._id, buildBody() as Partial<ICoupon>);
      trackEvent('coupon_updated');
      toast.success('נשמר');
      emit('updated', updated);
    } else {
      const created = await PromotionsApi.createCoupon(buildBody() as Partial<ICoupon>);
      trackEvent('coupon_created');
      toast.success('הקופון נוצר');
      emit('created', created);
    }
    emit('close');
  } catch (e) {
    if (e === 'DUPLICATE') toast.error('קוד הקופון כבר קיים');
    else toast.error('שגיאה בשמירה');
  } finally {
    loading.value = false;
  }
};

const remove = () => {
  if (!props.coupon?._id) return;
  confirm.require({
    message: 'למחוק את הקופון?',
    header: 'רק מוודאים',
    rejectLabel: 'לא',
    acceptLabel: 'כן',
    blockScroll: true,
    accept: async () => {
      deleting.value = true;
      try {
        await PromotionsApi.deleteCoupon(props.coupon!._id!);
        trackEvent('coupon_deleted');
        toast.success('הקופון נמחק');
        emit('deleted', props.coupon!._id);
        emit('close');
      } catch (e) {
        toast.error('שגיאה');
      } finally {
        deleting.value = false;
      }
    }
  });
};

onMounted(() => {
  if (props.coupon) {
    Object.assign(form, {
      ...props.coupon,
      // Coupons created before entity scoping existed have no list — which already means
      // "applies to everything".
      applicableEntityTypes: props.coupon.applicableEntityTypes ?? [],
      validFrom: props.coupon.validFrom ? new Date(props.coupon.validFrom) : undefined,
      validUntil: props.coupon.validUntil ? new Date(props.coupon.validUntil) : undefined
    });
    if (props.coupon.applicableServiceIds?.length) {
      const allServices = adminStore.businessSiteData?.services ?? [];
      selectedServices.value = allServices.filter(s => props.coupon!.applicableServiceIds!.includes(s.id));
    }
  }
});
</script>
